import React, { useEffect, useMemo, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Bell, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EnhancedSidebar } from '@/components/common/EnhancedSidebar';
import { useIsAuthenticated } from '@/hooks/redux';
import { cn, formatNumberWithCommas, showToastMessage } from '@/utils';
import { RootState } from '@/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  useAvailbleRolesQuery,
  useGetUserQuery,
  useGetWalletQuery,
  useLogoutMutation,
} from '@/store/api/authApi';
import { clearUser } from '@/store/slices/userSlice';
import { logout as clearAuth } from '@/store';
import apiSlice from '@/store/api/apiSlice';
import { STATUS_LOGOUT_MESSAGES } from '@/utils/constent';
import { NotificationSidebar } from '@/components/common/NotificationSidebar';
import { useGetUnreadNotificationsQuery } from '@/store/api/notificationApi';
import { NewNotificationBanner } from '@/components/common/NewNotificationBanner';
import { Modal } from '@/components/common/Modal';
import { ChangePassword } from '@/components/common/ChangePassword';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { useRoleCheck } from '@/hooks/useRoleCheck';
import { useGetMfaStatusQuery } from '@/store/api/mfaApi';
import MfaManager from '@/pages/settings/MfaManager';
export function DashboardLayout() {
  const [logout] = useLogoutMutation();
  const BLOCKED_STATUSES = ['inactive', 'suspended'];
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const ismobile = window.innerWidth < 768;
  const [sidebarOpen, setSidebarOpen] = React.useState(!ismobile);
  const [notifOpen, setNotifOpen] = React.useState(false);
  const [bannerVisible, setBannerVisible] = React.useState(false);
  const isAuthenticated = useIsAuthenticated();
  const { canUseMfa } = useRoleCheck();
  const [mfaModalOpen, setMfaModalOpen] = React.useState(false);
  const { data: mfaStatus } = useGetMfaStatusQuery(undefined, { skip: !canUseMfa });
  const prevUnreadRef = React.useRef<number | null>(null);

  const { data: userData } = useGetUserQuery(undefined, {
    skip: !isAuthenticated,
    pollingInterval: 20000,
  });

  const prevUnreadCountRef = useRef<number | null>(null);
  const [isBellRinging, setIsBellRinging] = React.useState(false);

  const { data: unreadData } = useGetUnreadNotificationsQuery(undefined, {
    skip: !isAuthenticated,
    pollingInterval: 10000,
  });
  const unreadNotifications = unreadData?.notifications ?? [];
  const unreadCount = unreadNotifications.length;
  const latestNotifTitle = unreadNotifications[0]?.notification ?? '';
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    // Ignore first load
    if (prevUnreadCountRef.current === null) {
      prevUnreadCountRef.current = unreadCount;
      return;
    }

    // New notification received
    if (unreadCount > prevUnreadCountRef.current) {
      const audio = new Audio('/images/notification.mp3');

      audio.play().catch(() => {
        // Browser may block autoplay
      });

      setIsBellRinging(true);

      timer = setTimeout(() => {
        setIsBellRinging(false);
      }, 3000);
    }

    prevUnreadCountRef.current = unreadCount;

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [unreadCount]);
  useEffect(() => {
    if (prevUnreadRef.current !== null && unreadCount > prevUnreadRef.current) {
      setBannerVisible(true);
    }
    prevUnreadRef.current = unreadCount;
  }, [unreadCount]);
  const [userClosed, setUserClosed] = React.useState(false);

  useAvailbleRolesQuery(undefined, { skip: !isAuthenticated });
  const { data } = useGetWalletQuery(undefined, { skip: !isAuthenticated, pollingInterval: 20000 });
  const user = useSelector((state: RootState) => state.user);
  const closeModal = () => setUserClosed(true);
  const mainAmount = useMemo(() => {
    return data?.find((w) => w.type === 'Main')?.amount ?? '0';
  }, [data]);
  const showModal = Boolean(user.data && user.data.passwordChanged === false && !userClosed);
  useEffect(() => {
    if (!userData?.status) return;

    if (BLOCKED_STATUSES.includes(userData.status.toLowerCase())) {
      // 🔹 Clear auth (example)
      const message =
        STATUS_LOGOUT_MESSAGES[userData?.status.toLowerCase()] ??
        'Your session has ended. Please log in again.';

      handleLogout(message);
      navigate('/login', { replace: true });
    }
  }, [userData?.status, navigate]);
  const handleLogout = async (message: string) => {
    try {
      await logout().unwrap();
      dispatch(clearAuth());
      dispatch(clearUser());
      apiSlice.util.resetApiState();

      showToastMessage('error', message);
      navigate('/login');
    } catch (err: any) {
      showToastMessage('error', err?.data?.message || 'Failed to logout. Please try again.');
    }
  };

  const profileBlock = (
    <div className="border border-green3 rounded-lg flex-end ml-auto gap-2  px-4 ">
      <img src="/images/profile.png" alt="" className="h-10 w-10" />
      <div className="flex flex-col justify-start items-start py-1 font-semibold">
        <span className="leading-4">{user?.data?.username || 'Owner'}</span>
        <span className="flex items-center">{formatNumberWithCommas(mainAmount)}</span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background ">
      {/* Enhanced Sidebar */}
      <EnhancedSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />

      {/* Main Content */}
      <div className={cn('transition-all duration-300', sidebarOpen ? 'lg:ml-72' : 'lg:ml-16')}>
        {/* Top Navigation */}
        <header className="sticky top-0 z-40 border-b border-green3 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex h-16 items-center px-4 bg-black text-white">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className=""
              // className="md:hidden"
            >
              <Menu className="h-5 w-5" />
            </Button>

            <div className="ml-auto gap-1 md:gap-3 flex items-center justify-end">
              {/* Notification Bell */}
              <button
                onClick={() => setNotifOpen(true)}
                className="relative ml-auto mr-3 text-white hover:text-purple3 transition-colors"
                aria-label="Notifications"
              >
                <Bell className={cn('h-6 w-6', unreadCount > 0 && 'animate-bell')} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-purple3 text-[10px] font-bold text-white leading-none">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Stays controlled by the server: it only flips once the flow has succeeded. */}
              {canUseMfa && mfaStatus && (
                <div className="mr-3 flex items-center gap-2">
                  <span className="text-sm font-medium">MFA</span>
                  <Switch
                    aria-label="Toggle MFA"
                    checked={mfaStatus.isMfaEnabled}
                    onCheckedChange={() => setMfaModalOpen(true)}
                  />
                </div>
              )}

              {canUseMfa ? (
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <button type="button" aria-label="Profile menu" className="ml-auto">
                      {profileBlock}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => navigate('/mfa-settings')}>
                      Security
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                profileBlock
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 py-2 sm:py-6 bg-main-bg min-h-[calc(100dvh-65px)]">
          <Outlet />
        </main>
      </div>
      <NotificationSidebar open={notifOpen} onClose={() => setNotifOpen(false)} />
      <NewNotificationBanner
        visible={bannerVisible}
        title={latestNotifTitle}
        onOpen={() => {
          setBannerVisible(false);
          setNotifOpen(true);
        }}
        onDismiss={() => setBannerVisible(false)}
      />

      <Modal
        open={mfaModalOpen}
        onOpenChange={setMfaModalOpen}
        title="Two-Factor Authentication"
        closeBtn={<X />}
      >
        <MfaManager startOpen onDone={() => setMfaModalOpen(false)} />
      </Modal>

      {showModal && (
        <Modal
          open={showModal}
          style="top-[25%] left-1/2 -translate-x-1/2 border-purple rounded-2xl shadow-sm"
          bgBlur={true}
        >
          <ChangePassword
            close={closeModal}
            title={true}
            inputClass="bg-white border-gold-light text-black"
            lableClass="text-black"
          />
        </Modal>
      )}
    </div>
  );
}
